import { useState } from 'react';
import { useMutation, useQueryClient, useSuspenseQuery } from '@tanstack/react-query';
import { Link } from '@tanstack/react-router';

import { Badge } from '@/components/ui';
import { adminPermissionsQuery, adminUsersQuery, meQuery } from '@/queries';

/**
 * Who works here, and what they are. Spec 003 §9, FR-805.
 *
 * The companion to the permissions screen: that one sets what a ROLE may do,
 * this one sets who HAS each role. Together they are what the client asked for.
 *
 * **There is no delete.** Q-18: the records of who received the goods, who
 * reported the incident and who opened the count all name a person and refuse
 * to let go, correctly. So the action is Deactivate: the role comes off, every
 * internal door closes, and the history keeps their name. The screen says that
 * rather than offering a button that would fail.
 */

const ROLE_LABEL: Record<string, string> = {
  admin: 'Administrator',
  manager: 'Manager',
  staff: 'Staff',
  b2b: 'Trade customer',
  b2c: 'Retail customer',
};

const INTERNAL = ['admin', 'manager', 'staff'];

export default function AdminUsersPage() {
  const qc = useQueryClient();
  const { users } = useSuspenseQuery(adminUsersQuery).data;
  const { roles } = useSuspenseQuery(adminPermissionsQuery).data;
  const me = useSuspenseQuery(meQuery).data;
  const [error, setError] = useState<string | null>(null);

  const admins = users.filter((u) => u.role === 'admin').length;

  const change = useMutation({
    mutationFn: async ({ id, role }: { id: string; role: string }) => {
      const res = await fetch(`/api/users/${id}/role`, {
        method: 'PATCH',
        credentials: 'same-origin',
        headers: { 'content-type': 'application/json' },
        body: JSON.stringify({ role }),
      });
      if (!res.ok) {
        const body = (await res.json().catch(() => null)) as
          | { error?: { message?: string } } | null;
        throw new Error(body?.error?.message ?? `Request failed (${res.status})`);
      }
      return null;
    },
    onSuccess: () => {
      setError(null);
      void qc.invalidateQueries({ queryKey: ['admin', 'users'] });
    },
    onError: (e: Error) => setError(e.message),
  });

  const deactivate = useMutation({
    mutationFn: async (id: string) => {
      const res = await fetch(`/api/users/${id}/deactivate`, {
        method: 'POST',
        credentials: 'same-origin',
      });
      if (!res.ok) {
        const body = (await res.json().catch(() => null)) as
          | { error?: { message?: string } } | null;
        throw new Error(body?.error?.message ?? `Request failed (${res.status})`);
      }
      return null;
    },
    onSuccess: () => {
      setError(null);
      void qc.invalidateQueries({ queryKey: ['admin', 'users'] });
    },
    onError: (e: Error) => setError(e.message),
  });

  return (
    <div>
      <div className="mb-6">
        <h1 className="text-[22px] font-semibold tracking-[-0.01em]">People</h1>
        <p className="mt-1 text-[12.5px] text-muted">
          Who has an account, and what kind. What each kind can do is on{' '}
          <Link to="/admin/permissions" className="underline">
            Permissions
          </Link>
          .
        </p>
      </div>

      {admins < 2 && (
        <div className="mb-5 rounded-[12px] border border-line bg-panel p-4 text-[14px]">
          <strong>There is only one administrator.</strong> The system will not let
          you remove the last one, which means losing access to that account
          means losing the ability to administer this system at all. A second
          administrator costs nothing and removes that.
        </div>
      )}

      {error && (
        <p className="mb-4 rounded-[10px] border border-line bg-panel p-3 text-[14px]">{error}</p>
      )}

      <div className="rounded-[10px] border border-line bg-panel">
        {users.length === 0 ? (
          <p className="p-8 text-center text-[15px] text-muted">
            Nobody has an account yet.
          </p>
        ) : (
          users.map((u) => {
            const isMe = u.user_id === me.user?.id;
            return (
              <div
                key={u.user_id}
                className="flex flex-wrap items-center justify-between gap-3 border-b
                           border-line px-4 py-3 last:border-0"
              >
                {/* An email is one long word, and a word that cannot break
                    widens the page on a phone: 115 px, from a test account's
                    address (30 October). `anywhere` lets it wrap, and
                    `min-w-0` lets the column be narrower than 240 px when the
                    screen is. */}
                <span className="flex min-w-0 grow basis-[240px] flex-col [overflow-wrap:anywhere]">
                  <span className="font-semibold">
                    {u.name ?? u.email ?? 'Unnamed'}
                    {isMe && <span className="ml-2 text-[13px] text-muted">you</span>}
                  </span>
                  <span className="text-[13px] text-muted">
                    {u.email}
                    {u.last_sign_in_at
                      ? ` · last signed in ${new Date(u.last_sign_in_at).toLocaleDateString('en-AU')}`
                      : ' · never signed in'}
                  </span>
                </span>

                {/* Wraps: the badge, the role and Deactivate are 330 px in a
                    row, wider than the row on a phone (pipeline 151). */}
                <span className="flex flex-wrap items-center gap-3">
                  {INTERNAL.includes(u.role) ? (
                    <Badge tone="brand">{ROLE_LABEL[u.role] ?? u.role}</Badge>
                  ) : (
                    <Badge tone="ok">{ROLE_LABEL[u.role] ?? u.role}</Badge>
                  )}

                  <select
                    value={u.role}
                    disabled={isMe || change.isPending}
                    aria-label={`Role for ${u.email ?? u.user_id}`}
                    onChange={(e) => change.mutate({ id: u.user_id, role: e.target.value })}
                    className="h-9 rounded-[8px] border border-line bg-panel px-2 text-[14px]"
                  >
                    {roles.map((r) => (
                      <option key={r.key} value={r.key}>{r.name}</option>
                    ))}
                  </select>

                  <button
                    type="button"
                    disabled={isMe || deactivate.isPending}
                    onClick={() => deactivate.mutate(u.user_id)}
                    className="rounded-[10px] border border-line px-3 py-2 text-[14px] text-muted disabled:opacity-40"
                  >
                    Deactivate
                  </button>
                </span>
              </div>
            );
          })
        )}
      </div>

      <p className="mt-4 max-w-[64ch] text-[13px] text-muted">
        Deactivating removes somebody's access and keeps their name on the work
        they did. There is no delete, on purpose: a stocktake that was approved
        by nobody, or a delivery received by nobody, is worth less as a record
        than one that still says who.
      </p>
    </div>
  );
}
